// src/pages/HelpCenterPage.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

const FAQS = [
  {
    category: "Orders & Shipping",
    q: "How do I track my order?",
    a: "Once your order ships, you'll receive an email with the tracking number. You can also view tracking info under Account → My Orders.",
  },
  {
    category: "Orders & Shipping",
    q: "How long does delivery take?",
    a: "Delivery times vary by seller and destination. Domestic US orders typically arrive in 3–5 business days. International orders can take 7–21 days.",
  },
  {
    category: "Orders & Shipping",
    q: "Can I change my shipping address after ordering?",
    a: "Contact the seller immediately through your order page. If the order hasn't shipped, they can usually update it.",
  },
  {
    category: "Returns",
    q: "What's your return policy?",
    a: "Most products can be returned within 30 days of delivery. Some categories (food, personalized items) are final sale. Check the return policy on each product page.",
  },
  {
    category: "Returns",
    q: "How do I start a return?",
    a: "Go to Account → My Orders → select the item → click Request Return. The seller will respond within 2 business days.",
  },
  {
    category: "Payments",
    q: "What payment methods do you accept?",
    a: "We accept major credit cards (Visa, Mastercard, Amex), PayPal, and in select regions VNPay and MoMo.",
  },
  {
    category: "Payments",
    q: "Why was my card declined?",
    a: "Check that the billing address matches your card and your card has sufficient funds. Contact your bank if it continues.",
  },
  {
    category: "Authenticity",
    q: "How do I know products are authentic?",
    a: "Every product on JepTags has an NFC tag or QR code linked to the brand. Scan it with any phone to verify authenticity.",
  },
  {
    category: "Authenticity",
    q: "What if I receive a counterfeit?",
    a: "Contact us immediately. JepTags guarantees 100% authentic products — we'll refund you in full and investigate the seller.",
  },
  {
    category: "Account",
    q: "How do I reset my password?",
    a: "Click 'Sign In' → 'Forgot password?' We'll email you a reset link within a few minutes.",
  },
];

export default function HelpCenterPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [openIdx, setOpenIdx] = useState(null);

  const filtered = FAQS.filter(f => {
    const s = search.toLowerCase();
    return f.q.toLowerCase().includes(s) || f.a.toLowerCase().includes(s) || f.category.toLowerCase().includes(s);
  });

  // Group by category
  const grouped = filtered.reduce((acc, f) => {
    (acc[f.category] = acc[f.category] || []).push(f);
    return acc;
  }, {});

  return (
    <div className="hc-root">
      <style dangerouslySetInnerHTML={{ __html: STYLES }} />

      {/* HERO */}
      <section className="hc-hero">
        <button className="hc-back" onClick={() => navigate(-1)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <div className="hc-badge">Help Center</div>
        <h1>How can we <span className="grad">help?</span></h1>
        <p className="hc-lede">Browse common questions or contact our support team.</p>

        {/* Search */}
        <div className="hc-search">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            type="text"
            placeholder="Search help articles..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </section>

      {/* FAQ SECTIONS */}
      <section className="hc-body">
        {Object.keys(grouped).length === 0 && (
          <div className="hc-empty">
            No articles match "{search}".<br/>
            <a href="mailto:support@jeptags.com">Contact support</a> and we'll help directly.
          </div>
        )}

        {Object.entries(grouped).map(([category, items]) => (
          <div key={category} className="hc-category">
            <h2>{category}</h2>
            <div className="hc-faq-list">
              {items.map((f, i) => {
                // unique index across categories
                const globalIdx = `${category}-${i}`;
                const isOpen = openIdx === globalIdx;
                return (
                  <div key={globalIdx} className={`hc-faq ${isOpen ? 'open' : ''}`}>
                    <button
                      className="hc-faq-q"
                      onClick={() => setOpenIdx(isOpen ? null : globalIdx)}
                      aria-expanded={isOpen}
                    >
                      <span>{f.q}</span>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </button>
                    {isOpen && <div className="hc-faq-a">{f.a}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </section>

      {/* CONTACT CTA */}
      <section className="hc-contact">
        <h2>Still need help?</h2>
        <p>Our support team responds within 24 hours.</p>
        <div className="hc-contact-cards">
          <a href="mailto:support@jeptags.com" className="hc-contact-card">
            <div className="hc-contact-icon">✉️</div>
            <div>
              <div className="hc-contact-title">Email support</div>
              <div className="hc-contact-val">support@jeptags.com</div>
            </div>
          </a>
          <a href="tel:+18447374799" className="hc-contact-card">
            <div className="hc-contact-icon">📞</div>
            <div>
              <div className="hc-contact-title">Call us</div>
              <div className="hc-contact-val">+1-844-737-4799</div>
            </div>
          </a>
        </div>
      </section>
    </div>
  );
}

const STYLES = `
.hc-root *,.hc-root *::before,.hc-root *::after{margin:0;padding:0;box-sizing:border-box}
.hc-root{
  --cyan:#00E5CC;--cyan-bright:#00FFE0;--teal:#22D97F;
  --navy-deep:#030a14;--navy:#0a1628;--navy-mid:#0F2140;
  font-family:'Sora',sans-serif;background:var(--navy-deep);color:#e0f0f0;min-height:100vh;
}
.hc-root a{color:var(--cyan);text-decoration:none}
.grad{background:linear-gradient(135deg,var(--cyan-bright),var(--cyan),var(--teal));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}

/* HERO */
.hc-hero{max-width:860px;margin:0 auto;padding:60px 24px 48px;text-align:center;position:relative}
.hc-back{position:absolute;top:60px;left:24px;display:inline-flex;align-items:center;gap:6px;background:none;border:1px solid rgba(0,229,204,0.15);color:rgba(224,240,240,0.6);font-family:'Sora',sans-serif;font-size:13px;padding:8px 16px;border-radius:8px;cursor:pointer;transition:all .3s}
.hc-back:hover{border-color:var(--cyan);color:var(--cyan)}
.hc-back svg{width:14px;height:14px}
.hc-badge{display:inline-block;padding:8px 20px;border-radius:100px;background:rgba(0,229,204,0.08);border:1px solid rgba(0,229,204,0.15);font-size:12px;font-weight:600;letter-spacing:1.5px;text-transform:uppercase;color:var(--cyan);margin-bottom:20px}
.hc-hero h1{font-size:clamp(36px,5vw,52px);font-weight:800;line-height:1.1;letter-spacing:-1.5px;margin-bottom:14px}
.hc-lede{font-size:16px;color:rgba(224,240,240,0.55);margin-bottom:32px}

/* SEARCH */
.hc-search{position:relative;max-width:560px;margin:0 auto}
.hc-search svg{position:absolute;left:18px;top:50%;transform:translateY(-50%);width:20px;height:20px;color:rgba(224,240,240,0.25)}
.hc-search input{width:100%;padding:16px 20px 16px 52px;border-radius:14px;border:1.5px solid rgba(0,229,204,0.1);background:rgba(15,33,64,0.5);color:#e0f0f0;font-family:'Sora',sans-serif;font-size:15px;outline:none;transition:all .3s}
.hc-search input::placeholder{color:rgba(224,240,240,0.25)}
.hc-search input:focus{border-color:var(--cyan);box-shadow:0 0 0 3px rgba(0,229,204,0.1)}

/* BODY */
.hc-body{max-width:860px;margin:0 auto;padding:0 24px 40px}
.hc-empty{padding:60px 24px;text-align:center;color:rgba(224,240,240,0.5);font-size:15px;line-height:1.8}
.hc-category{margin-bottom:40px}
.hc-category h2{font-size:20px;font-weight:700;color:#e0f0f0;margin-bottom:16px;letter-spacing:-.3px}

.hc-faq-list{display:flex;flex-direction:column;gap:8px}
.hc-faq{background:rgba(15,33,64,0.5);border:1px solid rgba(0,229,204,0.06);border-radius:14px;overflow:hidden;transition:border-color .2s}
.hc-faq.open{border-color:rgba(0,229,204,0.2)}
.hc-faq-q{width:100%;padding:18px 22px;background:none;border:none;color:#e0f0f0;font-family:'Sora',sans-serif;font-size:15px;font-weight:600;text-align:left;cursor:pointer;display:flex;align-items:center;justify-content:space-between;gap:16px;transition:color .2s}
.hc-faq-q:hover{color:var(--cyan)}
.hc-faq-q svg{width:18px;height:18px;color:rgba(224,240,240,0.5);transition:transform .25s;flex-shrink:0}
.hc-faq.open .hc-faq-q svg{transform:rotate(180deg)}
.hc-faq-a{padding:0 22px 20px;font-size:14px;line-height:1.7;color:rgba(224,240,240,0.6)}

/* CONTACT */
.hc-contact{max-width:860px;margin:0 auto;padding:40px 24px 100px;text-align:center}
.hc-contact h2{font-size:28px;font-weight:800;letter-spacing:-.5px;margin-bottom:10px}
.hc-contact p{font-size:15px;color:rgba(224,240,240,0.5);margin-bottom:28px}
.hc-contact-cards{display:grid;grid-template-columns:1fr 1fr;gap:16px;max-width:560px;margin:0 auto}
.hc-contact-card{display:flex;align-items:center;gap:14px;padding:20px;background:rgba(15,33,64,0.5);border:1px solid rgba(0,229,204,0.1);border-radius:16px;transition:all .25s;text-align:left}
.hc-contact-card:hover{border-color:var(--cyan);transform:translateY(-2px)}
.hc-contact-icon{font-size:28px}
.hc-contact-title{font-size:12px;font-weight:700;letter-spacing:.5px;text-transform:uppercase;color:rgba(224,240,240,0.4);margin-bottom:4px}
.hc-contact-val{font-size:14px;color:var(--cyan);font-weight:600}

@media(max-width:700px){
  .hc-back{position:static;margin-bottom:20px}
  .hc-hero{padding:40px 20px 32px}
  .hc-contact-cards{grid-template-columns:1fr}
}
`;
